/**
 * What may be attached anywhere in the app, in one place (Jira NAV-89).
 *
 * Uploads reach two surfaces — a request's evaluation artifacts (PRD D8) and a
 * group contract's documents (PRD F3) — and both are read back the same way:
 * `getDownloadURL` mints a URL the browser then opens in a tab. **The bytes are
 * served with the content type recorded at upload**, so the type is not a
 * cosmetic label: an object stored as `text/html` or `image/svg+xml` executes
 * as a page on the storage origin when somebody opens it. That is the reason
 * this list is an allowlist of inert document and image formats rather than a
 * blocklist of the obviously nasty ones — a blocklist has to be complete to
 * work, and this one only has to be right.
 *
 * The content type is derived from the **file name's extension**, never from
 * `File.type`. Two reasons. `File.type` is whatever the operating system
 * guesses and is legitimately empty for a PDF on some machines, so trusting it
 * rejects real files; and it is the client's to set, so it is not a check
 * either. Deriving it from a finite table means what we upload is always a
 * member of the list `storage.rules` admits, and the served type is a value
 * this file chose rather than one the browser handed us.
 *
 * **Mirrored in `storage.rules` and `firestore.rules`, deliberately.** The
 * screen refusing a file is a courtesy; a hand-written upload skips it
 * entirely, and the rules are the boundary. `attachments.test.ts` reads both
 * rules files and asserts the three lists are the same set, so a format added
 * here without being added there fails rather than being quietly refused at
 * the boundary.
 */

import { firstNamed } from "@/utils/requests";

/**
 * The ceiling on one attachment, and a number the interface states rather than
 * one a rejected upload leaves somebody guessing at.
 *
 * 25 MB because that is the ceiling people already expect from mail, it covers
 * the scanned contracts and assessments these paths carry, and it bounds what a
 * single admin can push into the bucket — which is the other half of NAV-89.
 */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/** The same number in the unit the interface says it in. */
export const ATTACHMENT_MAX_MB = ATTACHMENT_MAX_BYTES / (1024 * 1024);

/**
 * Extension → the content type the object is stored and served as.
 *
 * Keyed by extension because that is what the check reads and what the stored
 * file name carries. Everything here renders inertly in a browser: documents
 * the browser hands to a viewer or downloads, and the two raster image formats.
 * Notably absent and meant to be: `.html`/`.htm` and `.svg` (they execute),
 * archives (nothing here needs to unpack one), and anything executable.
 */
const CONTENT_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xls": "application/vnd.ms-excel",
  ".xlsx":
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
};

/** The extensions an attachment may carry, in the order the UI lists them. */
export const ATTACHMENT_EXTENSIONS: readonly string[] = Object.keys(
  CONTENT_TYPE_BY_EXTENSION,
);

/**
 * The distinct content types an object may be stored as — what the rules files
 * admit, and what the guard in `attachments.test.ts` compares them against.
 * Sorted so the two sides of that comparison are order-independent.
 */
export const ATTACHMENT_CONTENT_TYPES: readonly string[] = [
  ...new Set(Object.values(CONTENT_TYPE_BY_EXTENSION)),
].sort();

/** The `accept` attribute for a file input. A picker hint only — it narrows
 *  what the dialog offers and is trivially bypassed by a drag or a paste, so
 *  `checkAttachment` still runs on whatever arrives. */
export const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.join(",");

/**
 * The longest a stored file name may be. Bounded because the name goes into a
 * storage path and into a listing row; `firestore.rules` pins the same number.
 */
export const ATTACHMENT_FILENAME_MAX = 200;

/**
 * The longest a stored actor name may be, matching the bound `firestore.rules`
 * puts on a bug report's `addressedByName`.
 *
 * Truncated rather than refused: the name is a label the app composes itself
 * from the signed-in user, so a long display name is not their mistake to be
 * told about. A document's uploader is not named here at all — the rules
 * refuse a client-written `uploadedByName`, and a trigger writes it from the
 * uploader's account (NAV-189).
 */
export const ATTACHMENT_UPLOADER_NAME_MAX = 200;

/** A stored actor name that the rules will accept.
 *
 *  `firstNamed` and not `??` for the same reason the group-contract decision
 *  uses it: `??` stops at a blank, so an account whose Firebase `displayName`
 *  is `""` would have `""` stored, with the uid available and never reached. */
export function uploaderName(
  displayName?: string | null,
  email?: string | null,
  uid?: string,
): string {
  return firstNamed(displayName, email, uid).slice(
    0,
    ATTACHMENT_UPLOADER_NAME_MAX,
  );
}

/** Why a file was refused. The caller turns this into a sentence — the whole
 *  point of NAV-89's third bullet is that "rejected" is not an explanation, so
 *  each of these has its own, and "too large" never stands in for "empty". */
export type AttachmentRejection = "TYPE" | "SIZE" | "EMPTY" | "NAME";

export type AttachmentCheck =
  | { ok: true; contentType: string }
  | { ok: false; reason: AttachmentRejection };

/** Thrown by an upload path when `checkAttachment` refused the file, so the
 *  surface can say which of the rules it broke instead of "upload failed". */
export class AttachmentRejectedError extends Error {
  constructor(readonly reason: AttachmentRejection) {
    super(`attachment-rejected-${reason}`);
    this.name = "AttachmentRejectedError";
  }
}

/**
 * Which sentence explains each refusal.
 *
 * A literal map rather than a key assembled from the reason, because
 * `localeKeys.test.ts` can only see keys written out in full: an interpolated
 * `t()` argument matches its scan not at all, and a missing translation would
 * then render its own key at the one moment the interface has something to
 * explain. `attachments.test.ts` asserts every value here resolves in both
 * bundles, which is the same guard by another route.
 */
export const ATTACHMENT_REJECTION_KEYS: Readonly<
  Record<AttachmentRejection, string>
> = {
  TYPE: "common:requestDocuments.rejectedType",
  SIZE: "common:requestDocuments.rejectedSize",
  EMPTY: "common:requestDocuments.rejectedEmpty",
  NAME: "common:requestDocuments.rejectedName",
};

/** What the refusal sentences and the "what you may attach" line interpolate.
 *  One object so the limit is stated identically wherever it is stated. */
export const ATTACHMENT_LIMIT_PARAMS = {
  types: ATTACHMENT_EXTENSIONS.join(", "),
  max: ATTACHMENT_MAX_MB,
  maxName: ATTACHMENT_FILENAME_MAX,
} as const;

/** The lowercased extension of a file name, including the dot, or `""`. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  // A leading dot is the whole name of a dotfile, not an extension.
  return dot > 0 ? filename.slice(dot).toLowerCase() : "";
}

/**
 * The four refusals every upload surface makes, in the order they are made.
 *
 * One implementation because the three surfaces differ in exactly two things —
 * which extensions they admit and how large a file may be — and in one more
 * that only the attachment has: whether the file's own name reaches a storage
 * path. A third copy is where the divergence starts costing something, since a
 * fifth reason or a change to the name bound would otherwise have to land in
 * three places that nothing compares.
 *
 * The order is part of the contract: a zero-byte PDF on a surface that takes
 * no PDFs is told about the format, not about the size, because the format is
 * the thing they have to act on.
 */
function checkAgainst(
  file: File,
  byExtension: Readonly<Record<string, string>>,
  maxBytes: number,
  { rejectSlashes }: { rejectSlashes: boolean },
): AttachmentCheck {
  // An empty or overlong name has nowhere to render. A slash matters only
  // where the name reaches a storage path: there it would restructure the
  // path and then fail the rules' path-equality check with no explanation,
  // and where it does not reach one, refusing it would be telling somebody
  // they broke a rule that does not exist.
  if (
    file.name.length === 0 ||
    file.name.length > ATTACHMENT_FILENAME_MAX ||
    (rejectSlashes && file.name.includes("/"))
  ) {
    return { ok: false, reason: "NAME" };
  }
  const contentType = byExtension[extensionOf(file.name)];
  if (!contentType) return { ok: false, reason: "TYPE" };
  // A zero-byte file is a picker accident far more often than a real one, and
  // it lands in the listing looking like something somebody can open.
  if (file.size === 0) return { ok: false, reason: "EMPTY" };
  if (file.size > maxBytes) return { ok: false, reason: "SIZE" };
  return { ok: true, contentType };
}

/**
 * May this file be attached, and as what content type?
 *
 * Pure and synchronous, so a surface can refuse before any network call —
 * which is what makes the refusal instant and the message specific. Order
 * matters only in that a file failing two rules is reported by the first, and
 * the name is checked first because a file we cannot store under its own name
 * has no type to talk about.
 */
export function checkAttachment(file: File): AttachmentCheck {
  return checkAgainst(file, CONTENT_TYPE_BY_EXTENSION, ATTACHMENT_MAX_BYTES, {
    rejectSlashes: true,
  });
}

/**
 * A bug report's screenshot, which is a narrower thing than an attachment
 * (Jira NAV-141).
 *
 * Its own table rather than a subset of the one above expressed with a flag,
 * because the two answer different questions. An *attachment* is a document
 * somebody filed against a request — a DPA, an assessment, a scan — and that
 * list is wide because those arrive in whatever format the counterparty sent.
 * A *screenshot* is a raster capture a person has just taken of their own
 * screen, and the formats an operating system or a phone produces are the whole
 * of what this has to admit. Every other extension above would be a mis-pick
 * here, so it is refused with a sentence rather than stored.
 *
 * Both are inert, which is the property that carries over: the bytes come back
 * from `getDownloadURL` served as the type recorded at upload, on the storage
 * origin. `.svg` is a document that executes and is absent for exactly the
 * reason it is absent above.
 *
 * Mirrored in the `bug_reports/{uid}/{reportId}/screenshot` rule in
 * `storage.rules`, and `attachments.test.ts` reads that rule and asserts the
 * two agree — so a format added here alone is a file the screen offers and the
 * boundary refuses.
 */
const SCREENSHOT_CONTENT_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
};

/** The extensions a screenshot may carry, in the order the UI lists them. */
export const SCREENSHOT_EXTENSIONS: readonly string[] = Object.keys(
  SCREENSHOT_CONTENT_TYPE_BY_EXTENSION,
);

/** The distinct content types a screenshot may be stored as — what
 *  `storage.rules` admits, sorted so the comparison against it is
 *  order-independent. */
export const SCREENSHOT_CONTENT_TYPES: readonly string[] = [
  ...new Set(Object.values(SCREENSHOT_CONTENT_TYPE_BY_EXTENSION)),
].sort();

/** The `accept` attribute for the screenshot picker — a hint only, like
 *  `ATTACHMENT_ACCEPT`: a drag or a paste never sees it, which is why
 *  `checkScreenshot` still runs on whatever arrives. */
export const SCREENSHOT_ACCEPT = SCREENSHOT_EXTENSIONS.join(",");

/**
 * The ceiling on one screenshot.
 *
 * 5 MB rather than the attachment's 25: a full-screen PNG at a retina
 * resolution sits comfortably inside it, and this is a path **every** signed-in
 * account can write to from the dialog behind the account menu — a wider door
 * than the two admin surfaces attachments reach.
 */
export const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;

/** The same number in the unit the interface says it in. */
export const SCREENSHOT_MAX_MB = SCREENSHOT_MAX_BYTES / (1024 * 1024);

/** What the screenshot's own limits and refusal sentences interpolate. Its own
 *  object because the numbers differ from `ATTACHMENT_LIMIT_PARAMS`, and a
 *  sentence naming 25 MB beside a 5 MB refusal is worse than no sentence. */
export const SCREENSHOT_LIMIT_PARAMS = {
  types: SCREENSHOT_EXTENSIONS.join(", "),
  max: SCREENSHOT_MAX_MB,
  maxName: ATTACHMENT_FILENAME_MAX,
} as const;

/**
 * Which sentence explains each screenshot refusal.
 *
 * Three of the four are the attachment sentences, because they say the same
 * thing about a different list and a different number, and both arrive as
 * interpolations. The fourth is its own: the attachment version says "no
 * slashes, and at most N characters", and a screenshot has no slash rule to
 * break — its name never reaches a storage path (see {@link bugScreenshotPath}).
 * Telling somebody they broke a rule that does not exist here is the kind of
 * refusal NAV-89 set out to remove.
 *
 * A literal map, like its neighbour, because `localeKeys.test.ts` can only see
 * keys written out in full; `attachments.test.ts` asserts every value resolves
 * in both bundles and interpolates only what `SCREENSHOT_LIMIT_PARAMS` provides.
 */
export const SCREENSHOT_REJECTION_KEYS: Readonly<
  Record<AttachmentRejection, string>
> = {
  TYPE: "common:requestDocuments.rejectedType",
  SIZE: "common:requestDocuments.rejectedSize",
  EMPTY: "common:requestDocuments.rejectedEmpty",
  NAME: "common:requestDocuments.rejectedScreenshotName",
};

/**
 * Where a bug report's screenshot lives.
 *
 * **Derived, never stored**, and that is what makes the location safe to trust.
 * A `screenshotPath` field on the report would be a string the reporter's own
 * write put there, so it could name somebody else's object; both halves of this
 * path are facts the boundary already pins — the uid is `reportedById`, which
 * `firestore.rules` pins to the caller on create, and the report id is the
 * document's own name. So the reporter who wrote the report is the only person
 * whose screenshot it can be, with nothing to validate.
 *
 * One object per report, named `screenshot` and nothing else, so a report cannot
 * become a folder. No extension: the content type is recorded at upload and is
 * what `getDownloadURL` serves the bytes back as.
 *
 * `storage.rules` matches this exact shape and `attachments.test.ts` asserts the
 * two agree, so changing it here alone breaks the tests rather than the upload.
 */
export function bugScreenshotPath(
  reportedById: string,
  reportId: string,
): string {
  return `bug_reports/${reportedById}/${reportId}/screenshot`;
}

/**
 * May this file be attached to a bug report, and as what content type?
 *
 * The same four refusals as {@link checkAttachment}, in the same order, so the
 * sentences in `ATTACHMENT_REJECTION_KEYS` serve both surfaces — a reporter who
 * picked a PDF is told which formats work, not that their file was "rejected".
 *
 * **No slash check, unlike the attachment version, and that is a property of
 * the path rather than an omission.** A screenshot is stored under a fixed
 * object name (`…/{reportId}/screenshot`), so the file's own name never reaches
 * a storage path and cannot restructure one. The length bound stays because the
 * name is still what the extension — and therefore the served content type — is
 * read out of.
 */
export function checkScreenshot(file: File): AttachmentCheck {
  return checkAgainst(
    file,
    SCREENSHOT_CONTENT_TYPE_BY_EXTENSION,
    SCREENSHOT_MAX_BYTES,
    // The name never reaches a storage path: a screenshot is stored under a
    // fixed object name (see {@link bugScreenshotPath}).
    { rejectSlashes: false },
  );
}

/**
 * What a tool's logo may be uploaded as (Jira NAV-30, ADR 0018 decision 11).
 *
 * **Narrower than a screenshot's list, and for a different reason.** What ends
 * up served is a 128×128 PNG a function encoded, so the bytes here are only
 * ever decoded — never handed back to anybody. WebP joins PNG and JPEG because
 * it is what a vendor's own site increasingly serves and what a person
 * therefore has on disk; `.svg` is absent for the reason it is absent
 * everywhere above, and this list is not relying on the re-encode to excuse
 * it — a list that admits an executing document is one somebody copies to a
 * path with a read on it.
 *
 * Mirrored in the `tool_icon_uploads/{toolId}/{uploadId}` rule in
 * `storage.rules`, and `attachments.test.ts` reads that rule and asserts the
 * two agree.
 */
const LOGO_CONTENT_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
};

/** The extensions a logo may carry, in the order the UI lists them. */
export const LOGO_EXTENSIONS: readonly string[] = Object.keys(
  LOGO_CONTENT_TYPE_BY_EXTENSION,
);

/** The distinct content types the upload path admits, sorted so the comparison
 *  against `storage.rules` is order-independent. */
export const LOGO_CONTENT_TYPES: readonly string[] = [
  ...new Set(Object.values(LOGO_CONTENT_TYPE_BY_EXTENSION)),
].sort();

/** The `accept` attribute for the logo picker — a hint only, like the others:
 *  a drag or a paste never sees it, which is why `checkLogo` still runs on
 *  whatever arrives. */
export const LOGO_ACCEPT = LOGO_EXTENSIONS.join(",");

/**
 * The ceiling on one logo upload.
 *
 * 2 MB, which is small beside a screenshot's 5 and deliberately so: **this is a
 * bound on what we carry and decode, not on what we keep.** What is stored is
 * 128 pixels square however large the source was, so a bigger ceiling would buy
 * nothing but a longer wait and a larger thing to hand a decoder.
 */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/** The same number in the unit the interface says it in. */
export const LOGO_MAX_MB = LOGO_MAX_BYTES / (1024 * 1024);

/** What the logo picker's limits and refusal sentences interpolate. */
export const LOGO_LIMIT_PARAMS = {
  types: LOGO_EXTENSIONS.join(", "),
  max: LOGO_MAX_MB,
  maxName: ATTACHMENT_FILENAME_MAX,
} as const;

/**
 * Which sentence explains each logo refusal.
 *
 * **Its own four, not the attachment ones.** Sharing the screenshot's map was
 * cheaper and put *"That file type cannot be attached… Attach a smaller one"*
 * under a **Logo URL** box, where there is no attachment in sight — and the
 * first of those is also what a real `.png` the browser could not decode was
 * told. The refusals here are about a picture.
 *
 * A literal map, like its neighbours, because `localeKeys.test.ts` can only
 * see keys written out in full; `attachments.test.ts` asserts every value
 * resolves in both bundles and interpolates only what `LOGO_LIMIT_PARAMS`
 * provides.
 */
export const LOGO_REJECTION_KEYS: Readonly<Record<AttachmentRejection, string>> =
  {
    TYPE: "common:toolForm.logoRejectedType",
    SIZE: "common:toolForm.logoRejectedSize",
    EMPTY: "common:toolForm.logoRejectedEmpty",
    NAME: "common:toolForm.logoRejectedName",
  };

/**
 * Where an uploaded logo lands on its way in.
 *
 * **A quarantine, and the name says so.** Nobody may read this path; the only
 * thing that ever does is `on_tool_logo_uploaded`, which re-encodes the bytes,
 * writes the result where the catalogue reads it, and deletes what was left
 * here. So the file name never survives, which is why it is not in the path:
 * the object is named by a uuid the client mints, and `storage.rules` pins that
 * shape so the path space under a tool is bounded.
 */
export function toolLogoUploadPath(toolId: string, uploadId: string): string {
  return `tool_icon_uploads/${toolId}/${uploadId}`;
}

/**
 * May this file be uploaded as a tool's logo, and as what content type?
 *
 * The same four refusals in the same order as the two checks above, so the
 * sentences are shared. No slash check, for the reason `checkScreenshot` gives:
 * the file's own name never reaches a storage path. The length bound stays
 * because the name is still where the extension is read from.
 */
export function checkLogo(file: File): AttachmentCheck {
  return checkAgainst(file, LOGO_CONTENT_TYPE_BY_EXTENSION, LOGO_MAX_BYTES, {
    // Nor here: the object is named by a uuid the client mints, and the
    // uploaded file is deleted once it has been re-encoded.
    rejectSlashes: false,
  });
}
